import React, { useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import { enqueueSnackbar } from "notistack";

import GModal from "../design/components/GModal";
import Button from "../design/Button";
import { CustomInput } from "../design/components/customComponents";
import { JService } from "../api-service/ApiService";
import {
  checkboxCheck,
  checkboxUncheck,
} from "../../utility-functions/assetLoader";

import "./DeleteCallDataModal.css";
import { getOrgURLParam } from "../../utility-functions";

const CONFIRMATION_TEXT = "DELETE";

function DeleteCallDataModal({ visible, onClose, callIds = [] }) {
  //States
  const [deleteRecording, setDeleteRecording] = useState(true);
  const [deleteTranscript, setDeleteTranscript] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const isConfirmValid =
    confirmText.trim() === CONFIRMATION_TEXT &&
    (deleteRecording || deleteTranscript);

  //Handlers
  const handleDelete = () => {
    if (!isConfirmValid || isDeleting) return;
    setIsDeleting(true);
    JService.delete(`/api/v1/core/call-logs/delete-call-data/${getOrgURLParam()}`, {
      call_ids: callIds,
      delete_recording: deleteRecording,
      delete_transcript_and_insights: deleteTranscript,
    })
      .then((res) => {
        if (!res?.success)
          throw new Error(res?.message || "Failed to delete call data");
        enqueueSnackbar(res?.message || "Call data deleted successfully", {
          variant: "success",
        });
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || "Failed to delete call data", {
          variant: "error",
        });
      })
      .finally(() => {
        setIsDeleting(false);
        onClose();
      });
  };

  //Effects
  useEffect(() => {
    if (!visible) {
      setDeleteRecording(true);
      setDeleteTranscript(false);
      setConfirmText("");
      setIsDeleting(false);
    }
  }, [visible]);

  const body = (
    <div className="delete-call-data-modal-body">
      <p className="description">
        Select what you'd like to delete. Your call log will stay intact. This
        action can't be undone.
      </p>

      <label className="delete-option">
        <Checkbox
          icon={checkboxUncheck()}
          checkedIcon={checkboxCheck()}
          checked={deleteRecording}
          onChange={(e) => setDeleteRecording(e.target.checked)}
          disableRipple
          style={{ margin: 0, padding: 0 }}
        />
        <span className="option-label">Call recording</span>
      </label>

      <label className="delete-option">
        <Checkbox
          icon={checkboxUncheck()}
          checkedIcon={checkboxCheck()}
          checked={deleteTranscript}
          onChange={(e) => setDeleteTranscript(e.target.checked)}
          disableRipple
          style={{ margin: 0, padding: 0 }}
        />
        <span className="option-label">Transcript &amp; AI insights</span>
      </label>

      <p className="confirm-label">
        Type <b>&ldquo;{CONFIRMATION_TEXT}&rdquo;</b> to confirm
      </p>

      <CustomInput
        type="text"
        placeholder="Enter confirmation text"
        value={{ value: confirmText }}
        handleChange={(e) => setConfirmText(e.target.value)}
        labelStyles={{ width: "100%", marginBottom: 0 }}
        inputContainerStyle={{ width: "100%" }}
        inputProps={{
          className: "regular-font t7 confirm-input",
          autoFocus: true,
        }}
      />
    </div>
  );

  return (
    <GModal
      visible={visible}
      closeModal={onClose}
      disableCloseBtn={isDeleting}
      heading="Delete call data"
      body={body}
      modalStyle={{ width: "440px" }}
      bottomButtons={[
        <Button
          buttonText="Cancel"
          hierarchy="white"
          variant="outlined"
          isLoading={false}
          disabled={isDeleting}
          onClick={onClose}
        />,
        <Button
          buttonText="Delete"
          hierarchy="red"
          variant="contained"
          disabled={!isConfirmValid}
          isLoading={isDeleting}
          onClick={handleDelete}
        />,
      ]}
    />
  );
}

export default DeleteCallDataModal;
